import { useCallback, useEffect, useState } from "react"
import { Nav, OverlayTrigger, Tooltip } from "react-bootstrap"
import {
  useFetchList,
  VesselTrackingList,
} from "modules/VesselTracking/hooks/useFetchList"
import {
  faGear,
  faPlus,
  faPrint,
  faStar,
  faTags,
} from "@fortawesome/free-solid-svg-icons"
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ModalWithInput } from "modules/Shared/components/TabModals"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import {
  DEFAULT_SPEED_DATE_RANGE,
  DUPLICATE_SELECTED_TAB,
  GetCurrentDate,
  NEW_TAB,
  PrevetState,
  REMOVE_SELECTED_TAB,
  RENAME_SELECTED_TAB,
  SELECT_TAB,
} from "../hooks/prevetStateReducer"
import { ConstructPrevetTab } from "./PravetTabs"
import { PrevetMain } from "./PrevetMain"
import PrevetPortAlertModal from "./PrevetPortAlertModal"
import {
  AddVesselStruct,
  useAddVesselToList,
} from "modules/VesselTracking/hooks/useAddVesselToList"
import { VesselDetails } from "../hooks/usePrevetAPI"
import {
  getThreeMonthsFromNow,
  getSegment,
} from "modules/VesselTracking/helpers/helper"
import { toast } from "react-toastify"
import { useFetchVesselInfo } from "modules/VesselTracking/hooks/useFetchVesselInfo"
import { useDeleteVesselFromList } from "modules/VesselTracking/hooks/useDeleteVesselFromList"
import { SelectOption } from "modules/PoolPortal/helpers/types"
import PrevetTagVesselModal from "./PrevetTagVesselModal"
import { useFetchUnreadCount } from "../hooks/useFetchUnreadCount"
import { useMarkLogsAsRead } from "../hooks/useMarkLogsAsRead"
import { useSelector } from "react-redux"
import { RootState } from "store"
import { useWoopraAPI } from "instrumentation/tracking/useWoopraApi"

export function PrevetTabStateMain() {
  const { postTrackingEvent } = useWoopraAPI()
  const [currentPrevetTabStateName, setCurrentPrevetTabStateName] = useState("")
  const { markAsRead } = useMarkLogsAsRead()
  const [showDuplicate, setShowDuplicate] = useState(false)
  const [showRename, setShowRename] = useState(false)
  const [isFavorite, setIsFavorite] = useState(false)
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false)
  const { vesselTrackingList, fetchVesselTrackingList, getVesselListLoading } =
    useFetchList()
  const [vesselDetails, setVesselDetails] = useState<VesselDetails | null>(null)
  const [currentVesselSegment, setCurrentVesselSegment] = useState<
    string | null
  >(null)
  const [activeList, setActiveList] = useState<VesselTrackingList>()
  const { AddVesselToList } = useAddVesselToList()
  const { vesselInfos, fetchVesselInfo, isGetVesselInfoLoading } =
    useFetchVesselInfo()
  const { deleteVesselFromList } = useDeleteVesselFromList()
  const [showTagVesselModal, setShowTagVesselModal] = useState(false)

  const {
    reducerPrevetTabState,
    getSelectedIndex,
    setTabStateValue,
    getTabStateValue,
    prevetTabStateDispatch,
    isPortAlertAdmin,
    showPortAlertModal,
    setShowPortAlertModal,
  } = usePrevetTabStateContext()

  const newHandler = () => {
    prevetTabStateDispatch({ type: NEW_TAB })
  }

  const selectedImo = getTabStateValue("imo", getSelectedIndex())
  const imoString = selectedImo ? (selectedImo as SelectOption).value : ""
  const chosenOrganisation = useSelector(
    (state: RootState) => state.user.chosenOrganization
  )

  const { unreadCount, isSSEConnected } = useFetchUnreadCount(
    imoString,
    chosenOrganisation ? chosenOrganisation.id : ""
  )

  const renameHandler = useCallback(
    async (newName: string): Promise<void> => {
      try {
        if (reducerPrevetTabState) {
          const exist = reducerPrevetTabState.tabStates.find(
            (i) =>
              i.tabName === newName && i.id !== getSelectedIndex() && !i.deleted
          )
          if (exist) {
            return Promise.reject("Duplicate name")
          }
          prevetTabStateDispatch({
            type: RENAME_SELECTED_TAB,
            payload: { selectedIndex: getSelectedIndex(), name: newName },
          })
          return Promise.resolve()
        }
      } catch (err) {
        setShowRename(false)
        setCurrentPrevetTabStateName(newName)
        setShowRename(true)
        throw err
      }
    },
    [reducerPrevetTabState]
  )

  const duplicateHandler = useCallback(
    async (duplicateName: string): Promise<void> => {
      try {
        prevetTabStateDispatch({
          type: DUPLICATE_SELECTED_TAB,
          payload: getSelectedIndex(),
        })
        return Promise.resolve()
      } catch (err) {
        setShowDuplicate(false)
        setCurrentPrevetTabStateName(duplicateName)
        setShowDuplicate(true)
        throw err
      }
    },
    [reducerPrevetTabState]
  )

  const closeTabHandler = useCallback(
    async (id: number): Promise<void> => {
      prevetTabStateDispatch({ type: REMOVE_SELECTED_TAB, payload: id })
    },
    [reducerPrevetTabState]
  )

  const clearTabHandler = useCallback(
    async (id: number): Promise<void> => {
      setTabStateValue("imo", undefined, undefined)
      setTabStateValue("date", GetCurrentDate(), undefined)
      setTabStateValue("deleted", false, undefined)
      setTabStateValue("cvdLadenSpeed", "", undefined)
      setTabStateValue("cvdLadenFuelCon", "", undefined)
      setTabStateValue("cvdLadenSeaDays", "", undefined)
      setTabStateValue("cvdBallastSpeed", "", undefined)
      setTabStateValue("cvdBallastFuelCon", "", undefined)
      setTabStateValue("cvdBallastSeaDays", "", undefined)
      setTabStateValue("vesselSpeedGraphZoneType", undefined, undefined)
      setTabStateValue(
        "vesselSpeedDateRange",
        DEFAULT_SPEED_DATE_RANGE,
        undefined
      )
      setTabStateValue("perfExposureHireRate", "", undefined)
      setTabStateValue("perfExposureFuelPrice", "", undefined)
      setTabStateValue("perfExposureValue", "", undefined)
      setTabStateValue("hvIncludeBallast", true, undefined)
      setTabStateValue("hvIncludeLaden", true, undefined)
      setTabStateValue("hvSearchText", "", undefined)
      setTabStateValue("hvCurrentPage", 1, undefined)
      setTabStateValue("hvRowsPerPage", 10, undefined)
      setTabStateValue("hvSortByProp", "date_from", undefined)
      setTabStateValue("hvSortByOrder", "desc", undefined)
      setTabStateValue("showSeaMargin", true, undefined)
      setTabStateValue("ladenMarginOverride", undefined, undefined)
      setTabStateValue("ballastMarginOverride", undefined, undefined)
    },
    [reducerPrevetTabState]
  )

  const modalDecorator = useCallback(
    (wrappedFunc: void) => {
      const prevetTabStateName =
        (reducerPrevetTabState &&
        reducerPrevetTabState.tabStates[getSelectedIndex()]
          ? reducerPrevetTabState.tabStates[getSelectedIndex()].tabName
          : "") || ""
      setCurrentPrevetTabStateName(prevetTabStateName)
      return wrappedFunc
    },
    [reducerPrevetTabState]
  )

  //fetch vesseltracking lists
  useEffect(() => {
    fetchVesselTrackingList()
  }, [])

  //set prevet list as active list
  useEffect(() => {
    if (vesselTrackingList) {
      const tempList = vesselTrackingList.find(
        (vesselList) =>
          vesselList.listType === "prevet" && vesselList.orgId === null
      )
      setActiveList(tempList)
    }
  }, [vesselTrackingList])
  //set current vessel segment
  useEffect(() => {
    if (selectedImo) {
      const tempSegment = (selectedImo as SelectOption).label.toString()
      if (tempSegment) {
        setCurrentVesselSegment(getSegment(tempSegment))
      }
    }
  }, [selectedImo])

  //fetch vessel infos
  useEffect(() => {
    if (activeList) {
      const tempiDs = []
      tempiDs.push(activeList.id)
      fetchVesselInfo(tempiDs)
    }
  }, [activeList])

  //check if current vessel is already in favorites
  useEffect(() => {
    if (!selectedImo) {
      setIsFavorite(false)
      return
    }
    if (Array.isArray(vesselInfos)) {
      setIsFavorite(
        !!vesselInfos.find(
          (ves) =>
            ves.vesselImo === (selectedImo as SelectOption).value.toString()
        )
      )
    }
  }, [selectedImo, vesselInfos])

  //handle adding vessel imo
  const handleAddVesselToList = async (ves: AddVesselStruct) => {
    if (isFavoriteLoading) return
    setIsFavoriteLoading(true)
    if (activeList) {
      ves.listId = activeList.id
    }
    if (currentVesselSegment) {
      ves.vesselSegment = currentVesselSegment
    }
    if (vesselDetails) {
      ves.vesselImo = vesselDetails.imo.toString()
      ves.vesselDwt = vesselDetails.dwt.toString()
      ves.vesselName = vesselDetails.vessel_name
      ves.expirationDate = getThreeMonthsFromNow()
    }
    try {
      await AddVesselToList(ves, "prevetting")
      const message = `Added ${ves.vesselName} to favourites`
      toast.success(message, {
        position: "bottom-right",
        autoClose: 8000,
      })
      setIsFavorite(true)
    } catch (err: unknown) {
      const message = "Failed to add vessel to favourites"
      console.error(err)
      toast.error(message, {
        position: "bottom-right",
        autoClose: 8000,
      })
    } finally {
      await fetchVesselInfo(activeList ? [activeList.id] : [])
      setIsFavoriteLoading(false)
    }
  }

  const handleDeleteVesselFromList = async (vesselInfoID: string) => {
    if (isFavoriteLoading || !vesselInfoID) return
    setIsFavoriteLoading(true)
    try {
      await deleteVesselFromList(vesselInfoID, "prevetting")
      const message = "Vessel removed from favourites"
      toast.success(message, {
        position: "bottom-right",
        autoClose: 8000,
      })
      setIsFavorite(false)
    } catch (err: unknown) {
      const message = "Failed to remove vessel from favourites"
      console.error(err)
      toast.error(message, {
        position: "bottom-right",
        autoClose: 8000,
      })
    } finally {
      await fetchVesselInfo(activeList ? [activeList.id] : [])
      setIsFavoriteLoading(false)
    }
  }

  return (
    <>
      {/* modal */}
      <PrevetPortAlertModal
        show={showPortAlertModal}
        handleClose={() => {
          setShowPortAlertModal(false)
        }}
      />
      {/* tag modal */}
      <PrevetTagVesselModal
        show={showTagVesselModal}
        handleClose={() => setShowTagVesselModal(false)}
        vesselSegment={currentVesselSegment}
        vesselImo={
          selectedImo ? (selectedImo as SelectOption).value.toString() : null
        }
      />
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Nav
          variant="tabs"
          defaultActiveKey={"0"}
          onSelect={(selectedIndex: string | null) => {
            // Do not dispatch if selectedKey is not number - from command menus such "new", "rename" and etc.
            if (selectedIndex && !isNaN(Number(selectedIndex))) {
              prevetTabStateDispatch({
                type: SELECT_TAB,
                payload: +selectedIndex,
              })
            }
          }}
          activeKey={getSelectedIndex()}
          key="mainNav"
        >
          {ConstructPrevetTab(
            reducerPrevetTabState
              ? reducerPrevetTabState.tabStates
              : ([] as PrevetState[]),
            getSelectedIndex(),
            () => modalDecorator(setShowRename(true)),
            () => modalDecorator(setShowDuplicate(true)),
            closeTabHandler,
            clearTabHandler
          )}
          <button
            style={{
              marginLeft: "5px", // Space between tab and +
              background: "none", // No background
              border: "none", // No border
              fontSize: "1.2rem", // Font size for better visibility
              cursor: "pointer", // Pointer cursor on hover
            }}
            onClick={() => {
              postTrackingEvent({
                page: "/prevetting",
                event: "tab_new",
                properties: {
                  component: "prevet_tab",
                },
              })
              newHandler()
            }}
          >
            <FontAwesomeIcon icon={faPlus} className="fa-thin" size="xs" />
          </button>
        </Nav>

        <div style={{ paddingRight: "20px" }}>
          <div className="d-flex gap-2 align-items-center">
            {/* tag */}
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="trigger-tag">Tag vessel</Tooltip>}
            >
              <button
                style={{
                  cursor:
                    selectedImo == null || getVesselListLoading
                      ? "not-allowed"
                      : "pointer", // Pointer cursor on hover
                }}
                className="PrevetTagButton"
                disabled={
                  !selectedImo || getVesselListLoading || isGetVesselInfoLoading
                }
                onClick={() => {
                  setShowTagVesselModal(true)
                  markAsRead(
                    selectedImo
                      ? (selectedImo as SelectOption).value.toString()
                      : ""
                  )
                }}
              >
                <FontAwesomeIcon icon={faTags} size="lg" />
                {unreadCount > 0 && (
                  <span className="PrevetNotif">{unreadCount}</span>
                )}
              </button>
            </OverlayTrigger>
            {/* favorites */}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="trigger-favorite">Add to favourites</Tooltip>
              }
            >
              <button
                style={{
                  border: "none",
                  cursor:
                    selectedImo == null || getVesselListLoading || isFavoriteLoading
                      ? "not-allowed"
                      : "pointer",
                  backgroundColor: "transparent",
                }}
                disabled={
                  !selectedImo || getVesselListLoading || isGetVesselInfoLoading || isFavoriteLoading
                }
                onClick={
                  isFavorite
                    ? () => {
                        handleDeleteVesselFromList(
                          vesselInfos?.find(
                            (ves) =>
                              ves.vesselImo ===
                              (selectedImo as SelectOption)?.value.toString()
                          )?.id || ""
                        )
                        postTrackingEvent({
                          page: "/prevetting",
                          event: "click_favourites",
                          properties: {
                            vesselSegment: currentVesselSegment || "",
                            component: "right_icons",
                          },
                        })
                      }
                    : () => {
                        if (!selectedImo || getVesselListLoading) return
                        handleAddVesselToList({} as AddVesselStruct)
                      }
                }
              >
                {isFavorite ? (
                  <FontAwesomeIcon
                    icon={faStar}
                    size="lg"
                    style={{ color: "#FFD43B" }}
                  />
                ) : (
                  <FontAwesomeIcon icon={faStarRegular} size="lg" />
                )}
              </button>
            </OverlayTrigger>
            {/* port alerts */}
            {isPortAlertAdmin && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="button-tooltip">Edit port alerts</Tooltip>
                }
              >
                <button
                  style={{
                    border: "none", // No border
                    cursor: "pointer", // Pointer cursor on hover
                    backgroundColor: "transparent",
                  }}
                  onClick={() => {
                    setShowPortAlertModal(true)
                    postTrackingEvent({
                      page: "/prevetting",
                      event: "edit_port_alerts",
                      properties: {
                        vesselSegment: currentVesselSegment || "",
                        component: "right_icons",
                      },
                    })
                  }}
                >
                  <FontAwesomeIcon
                    icon={faGear}
                    className="fa-thin"
                    size="lg"
                  />
                </button>
              </OverlayTrigger>
            )}
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="save-print">Save/Print</Tooltip>}
            >
              <button
                style={{
                  border: "none", // No border
                  cursor: "pointer", // Pointer cursor on hover
                  backgroundColor: "transparent",
                }}
                onClick={() => {
                  captureScreen()
                  postTrackingEvent({
                    page: "/prevetting",
                    event: "capture_screen",
                    properties: {
                      vesselSegment: currentVesselSegment || "",
                      component: "right_icons",
                    },
                  })
                }}
              >
                <FontAwesomeIcon icon={faPrint} className="fa-thin" size="lg" />
              </button>
            </OverlayTrigger>
          </div>
        </div>
      </div>
      <PrevetMain
        selectedIndex={getSelectedIndex()}
        setVesselDetails={setVesselDetails}
      />
      {
        // Rename modal dialog box
        ModalWithInput(
          showRename,
          currentPrevetTabStateName,
          "Update tab name",
          "New tab name",
          renameHandler,
          () => setShowRename(false)
        )
      }
      {
        // Duplicate modal dialog box
        ModalWithInput(
          showDuplicate,
          currentPrevetTabStateName + " copy",
          "Duplicate tab",
          "Tab name",
          duplicateHandler,
          () => setShowDuplicate(false)
        )
      }
    </>
  )
}

// Function to capture the screen and either print or save as PDF
async function captureScreen(): Promise<void> {
  try {
    window.print()
  } catch (error) {
    console.error("Error capturing the screen:", error)
  }
}
